import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  AddressPropertiesSegmentSchema,
  CartValueSegmentSchema,
  CheckoutProbabilitySegmentSchema,
  CompoundNarySegment,
  CompoundNarySegmentSchema,
  CrossBorderShipmentSegmentSchema,
  CustomerReturnRateSegmentSchema,
  CustomerTagsSegmentSchema,
  DeliveryDistanceMilesSegmentSchema,
  DeliveryLocationNarySegment,
  DeliveryLocationNarySegmentSchema,
  DeliveryLocationRulesSegmentSchema,
  DeliveryLocationSegmentSchema,
  HistoricalOrderValueSegmentSchema,
  ItemQuantitySegmentSchema,
  LcgEligibleShipmentSegmentSchema,
  LineItemPropertiesSegmentSchema,
  OrderCountSegmentSchema,
  OriginLocationSegmentSchema,
  PoBoxShipmentSegmentSchema,
  ProductCollectionsSegmentSchema,
  ProductMetafieldsSegmentSchema,
  ProductSkuSegmentSchema,
  ProductTagsSegmentSchema,
  ProductVendorSegmentSchema,
  SegmentableCheckoutCharacteristic,
  ShipmentWeightSegmentSchema,
  SUBSCRIPTIONS_MODE_FORBIDDEN_SEGMENTS,
  TimeOfDaySegmentSchema,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  getDefaultNarySegment,
  getDefaultSegments,
  NarySegmentCharacteristic,
  narySegmentCharacteristics,
  segmentableCharacteristicLabel,
} from "@redotech/redo-conversion-core-common/checkouts/segments";
import {
  CheckoutOptimizationStep,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { FlowInProgress } from "@redotech/redo-flows-common/flow-in-progress";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { t, useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { isDef } from "@redotech/util/checks";
import { assertNever } from "@redotech/util/type";
import { ObjectId } from "bson";
import { memo, useMemo, useState } from "react";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "..";
import { NodeSelectionMode } from "../../flow-editor";
import { deleteElementAndAllChildren } from "../../utils/delete-element";
import { copySubtree } from "../../utils/selection-mode";
import { SegmentNameInput } from "../common/segment-name-input";
import {
  CompoundInspectorContent,
  EditableCompoundInspectorContent,
} from "./compound/compound-inspector";
import {
  DeliveryLocationNaryInspectorContent,
  EditableDeliveryLocationNaryInspectorContent,
} from "./delivery-location/delivery-location-nary-inspector";
import { SegmentTypeInspectorContent } from "./segment-type-editor";

function RcoSegmentInspector({
  step,
  setStep,
  readonly,
  steps,
  selectionMode,
  setAutomation,
  onClose,
}: StepTypeDetailsProps<PartialWithRequiredType<RcoSegmentStep>>) {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);

  const handleCharacteristicSelected = useHandler(
    ({
      characteristic,
      step,
    }: {
      characteristic: SegmentableCheckoutCharacteristic;
      step: PartialWithRequiredType<RcoSegmentStep>;
    }) => {
      if (narySegmentCharacteristics.has(characteristic)) {
        const defaultEdgeId = new ObjectId().toString();
        const edges: { edgeId: string; nextId: string }[] = [];
        const edgeIds: string[] = [];

        // Migrate nonMatchingNextId as a condition edge
        if (step.nonMatchingNextId) {
          const conditionEdgeId = new ObjectId().toString();
          edgeIds.push(conditionEdgeId);
          edges.push({
            edgeId: conditionEdgeId,
            nextId: step.nonMatchingNextId,
          });
        }

        // Always ensure at least one condition edge exists
        if (edgeIds.length === 0) {
          const conditionEdgeId = new ObjectId().toString();
          edgeIds.push(conditionEdgeId);
          edges.push({ edgeId: conditionEdgeId, nextId: "" });
        }

        // Migrate matchingNextId as the default edge
        edges.push({
          edgeId: defaultEdgeId,
          nextId: step.matchingNextId ?? "",
        });

        const newStep: PartialWithRequiredType<RcoSegmentStep> = {
          ...step,
          segment: getDefaultNarySegment(characteristic, edgeIds),
          edges,
          defaultEdgeId,
          matchingNextId: undefined,
          nonMatchingNextId: undefined,
        };
        setStep(newStep);
      } else {
        // Migrate n-ary to binary:
        // - default edge → nonMatchingNextId
        // - first condition edge → matchingNextId
        // - all other condition edges → recursively delete subtrees
        const defaultEdge = step.edges?.find(
          (e) => e.edgeId === step.defaultEdgeId,
        );
        const conditionEdges = (step.edges ?? []).filter(
          (e) => e.edgeId !== step.defaultEdgeId,
        );
        const firstConditionEdge = conditionEdges[0];
        const excessEdges = conditionEdges.slice(1);

        // Delete subtrees of excess condition edges
        if (setAutomation && excessEdges.length > 0) {
          setAutomation((prev) => {
            if (!prev) {
              return prev;
            }
            let updatedSteps = [...prev.steps];
            for (const edge of excessEdges) {
              if (edge.nextId) {
                updatedSteps = deleteElementAndAllChildren(
                  edge.nextId,
                  updatedSteps,
                );
              }
            }
            return { ...prev, steps: updatedSteps };
          });
        }

        const newStep: PartialWithRequiredType<RcoSegmentStep> = {
          ...step,
          segment:
            getDefaultSegments(team)[
              characteristic as Exclude<
                SegmentableCheckoutCharacteristic,
                NarySegmentCharacteristic
              >
            ],
          edges: undefined,
          defaultEdgeId: undefined,
          // Normalize "" to undefined so an unconnected arm renders a drop zone
          // rather than a dangling edge (which blanks the entire canvas).
          // "" passes ?? but not ||, so use || to fall through on empty strings.
          matchingNextId:
            (firstConditionEdge?.nextId || undefined) ?? step.matchingNextId,
          nonMatchingNextId:
            (defaultEdge?.nextId || undefined) ?? step.nonMatchingNextId,
        };
        setStep(newStep);
      }
    },
  );

  const [pendingCopy, setPendingCopy] = useState<CopyResult | null>(null);

  const applyCopyResult = useHandler((result: CopyResult) => {
    // Delete subtrees of children that no longer have a slot in the new
    // segment shape. Without this, switching from a wider n-ary segment to
    // a binary (or shorter n-ary) one leaves orphaned subtrees in the flow.
    if (setAutomation && result.excessChildIds.length > 0) {
      setAutomation((prev) => {
        if (!prev) {
          return prev;
        }
        let updatedSteps = [...prev.steps];
        for (const childId of result.excessChildIds) {
          updatedSteps = deleteElementAndAllChildren(childId, updatedSteps);
        }
        return { ...prev, steps: updatedSteps };
      });
    }
    setStep(result.newStep);
    toast(t("Successfully copied segment configuration."), {
      variant: "success",
      duration: Temporal.Duration.from({ seconds: 5 }),
    });
  });

  const onEnterNodeSelectMode = useHandler(() => {
    handleEnterNodeSelectMode({
      steps,
      setSelectionMode: selectionMode.setMode,
      step,
      onCopyComputed: (result) => {
        if (result.excessChildIds.length === 0) {
          applyCopyResult(result);
        } else {
          setPendingCopy(result);
        }
      },
    });
  });

  const onEnterSubtreeCopyMode = useHandler(() => {
    handleEnterSubtreeCopyMode({
      steps,
      setSelectionMode: selectionMode.setMode,
      step,
      setAutomation,
    });
  });

  const onExitNodeSelectMode = useHandler(() => {
    selectionMode.setMode({ type: "normal" });
  });

  const excludeCharacteristics = useMemo(() => {
    const excludeCharacteristics = new Set<SegmentableCheckoutCharacteristic>(
      [],
    );

    // Hide old DELIVERY_LOCATION unless it's already selected
    if (
      step.segment?.segmentableCharacteristic !==
      SegmentableCheckoutCharacteristic.DELIVERY_LOCATION
    ) {
      excludeCharacteristics.add(
        SegmentableCheckoutCharacteristic.DELIVERY_LOCATION,
      );
    }

    // DELIVERY_LOCATION_RULES is built programmatically (e.g. the landed-cost
    // demo) and has no standalone editor yet, so keep it out of the picker
    // unless it is already the selected characteristic.
    if (
      step.segment?.segmentableCharacteristic !==
      SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_RULES
    ) {
      excludeCharacteristics.add(
        SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_RULES,
      );
    }

    if (
      (!team.settings.predictiveConversion?.enabled ||
        !team.settings.predictiveConversion.activeCheckoutHazardModel
          ?.endpointName) &&
      step.segment?.segmentableCharacteristic !==
        SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY
    ) {
      excludeCharacteristics.add(
        SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY,
      );
    }

    if (
      !team.settings.landedCost?.enabled &&
      step.segment?.segmentableCharacteristic !==
        SegmentableCheckoutCharacteristic.LCG_ELIGIBLE_SHIPMENT
    ) {
      excludeCharacteristics.add(
        SegmentableCheckoutCharacteristic.LCG_ELIGIBLE_SHIPMENT,
      );
    }

    // Exclude forbidden segments when subscriptions mode is enabled
    if (team.settings.checkoutOptimization?.subscriptionsModeEnabled) {
      for (const characteristic of SUBSCRIPTIONS_MODE_FORBIDDEN_SEGMENTS) {
        excludeCharacteristics.add(characteristic);
      }
    }

    return excludeCharacteristics;
  }, [team, step.segment?.segmentableCharacteristic]);

  if (step.type !== StepType.RCO_SEGMENT) {
    return null;
  }

  return (
    <Flex align="flex-start" dir="column" gap="xl" pb="8xl">
      <Flex justify="space-between" w="full">
        <Flex align="center">
          <Text fontSize="md" fontWeight="semibold">
            {t("Segment")}
          </Text>
        </Flex>
        <RedoButton
          aria-label={t("Close")}
          hierarchy="tertiary"
          IconLeading={XIcon}
          onClick={onClose}
          size="sm"
        />
      </Flex>
      <SegmentNameInput
        onChange={(name) => setStep({ ...step, customTitle: name })}
        readonly={readonly}
        value={step.customTitle}
      />
      {!readonly && selectionMode.mode.type === "normal" && (
        <SegmentCharacteristicSelector
          excludeCharacteristics={excludeCharacteristics}
          onCharacteristicSelected={handleCharacteristicSelected}
          selectedCharacteristic={step.segment?.segmentableCharacteristic}
          step={step}
        />
      )}
      <InspectorContent
        readonly={readonly}
        selectionMode={selectionMode.mode}
        setAutomation={setAutomation}
        setStep={setStep}
        step={step}
      />
      {!readonly && (
        <ClickToCopyExistingSegment
          onEnterNodeSelectMode={onEnterNodeSelectMode}
          onEnterSubtreeCopyMode={onEnterSubtreeCopyMode}
          onExitNodeSelectMode={onExitNodeSelectMode}
          selectionMode={selectionMode.mode}
          step={step}
          steps={steps}
        />
      )}
      {pendingCopy && (
        <RedoModal
          isOpen
          modalSize="sm"
          onModalCloseRequested={() => setPendingCopy(null)}
          primaryButton={{
            text: t("Delete excess arms and copy", {
              count: pendingCopy.excessChildIds.length,
            }),
            size: "sm",
            theme: "destructive",
            onClick: () => {
              applyCopyResult(pendingCopy);
              setPendingCopy(null);
            },
          }}
          secondaryButton={{
            text: t("Cancel"),
            size: "sm",
            onClick: () => setPendingCopy(null),
          }}
          subtitle={
            t(
              "The source has {{count}} arms, but this segment has {{destArmCount}}.",
              {
                count: pendingCopy.sourceArmCount,
                destArmCount: pendingCopy.destArmCount,
              },
            ) +
            " " +
            t("Copying will permanently delete the {{count}} excess arms.", {
              count: pendingCopy.excessChildIds.length,
            })
          }
          title={t("Copy segment configuration?")}
        />
      )}
    </Flex>
  );
}

const InspectorContent = memo(function InspectorContent({
  step,
  setStep,
  readonly,
  selectionMode,
  setAutomation,
}: {
  step: PartialWithRequiredType<RcoSegmentStep>;
  setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  readonly: boolean | undefined;
  selectionMode: NodeSelectionMode;
  setAutomation?: React.Dispatch<React.SetStateAction<FlowInProgress | null>>;
}) {
  if (
    step.type !== StepType.RCO_SEGMENT ||
    !step.segment?.segmentableCharacteristic ||
    selectionMode.type !== "normal"
  ) {
    return null;
  }
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME

  switch (step.segment.segmentableCharacteristic) {
    case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026:
      if (!step.segment) {
        return null;
      }
      return readonly ? (
        <DeliveryLocationNaryInspectorContent
          segment={step.segment as DeliveryLocationNarySegment}
        />
      ) : (
        <EditableDeliveryLocationNaryInspectorContent
          setAutomation={setAutomation}
          setStep={setStep}
          step={step}
        />
      );
    case SegmentableCheckoutCharacteristic.COMPOUND:
      return readonly ? (
        <CompoundInspectorContent
          segment={step.segment as CompoundNarySegment}
        />
      ) : (
        <EditableCompoundInspectorContent
          setAutomation={setAutomation}
          setStep={setStep}
          step={step}
        />
      );
    default:
      return (
        <SegmentTypeInspectorContent
          readonly={readonly}
          setStep={setStep}
          step={step}
        />
      );
  }
});

const SegmentCharacteristicSelector = memo(
  function SegmentCharacteristicSelector({
    onCharacteristicSelected,
    selectedCharacteristic,
    step,
    excludeCharacteristics,
  }: {
    selectedCharacteristic: SegmentableCheckoutCharacteristic | undefined;
    onCharacteristicSelected: ({
      characteristic,
      step,
    }: {
      characteristic: SegmentableCheckoutCharacteristic;
      step: PartialWithRequiredType<RcoSegmentStep>;
    }) => void;
    step: PartialWithRequiredType<RcoSegmentStep>;
    excludeCharacteristics?: Set<SegmentableCheckoutCharacteristic>;
  }) {
    const { t } = useTranslation();
    const characteristicOptions: TextRedoListItem<SegmentableCheckoutCharacteristic>[] =
      useMemo(() => {
        return Object.values(SegmentableCheckoutCharacteristic)
          .filter(
            (characteristic) => !excludeCharacteristics?.has(characteristic),
          )
          .map((characteristic) => ({
            type: "text" as const,
            id: characteristic,
            value: characteristic,
            text: segmentableCharacteristicLabel[characteristic],
          }))
          .sort((a, b) => a.text.localeCompare(b.text));
      }, [excludeCharacteristics]);

    const selectedItem = useMemo(() => {
      return characteristicOptions.find(
        (option) => option.value === selectedCharacteristic,
      );
    }, [characteristicOptions, selectedCharacteristic]);

    return (
      <RedoSingleSelectDropdownInput<SegmentableCheckoutCharacteristic>
        fitToAnchor={false}
        label={t("Segment by")}
        options={characteristicOptions}
        optionSelected={({ item }) => {
          onCharacteristicSelected({ characteristic: item.value, step });
        }}
        placeholder={t("Select a segment characteristic")}
        selectedItem={selectedItem}
      />
    );
  },
);

interface CopyResult {
  newStep: PartialWithRequiredType<RcoSegmentStep>;
  excessChildIds: string[];
  sourceArmCount: number;
  destArmCount: number;
}

function handleEnterNodeSelectMode({
  steps,
  setSelectionMode,
  step,
  onCopyComputed,
}: {
  steps: PartialWithRequiredType<CheckoutOptimizationStep>[];
  setSelectionMode: (selectionMode: NodeSelectionMode) => void;
  step: PartialWithRequiredType<RcoSegmentStep>;
  onCopyComputed: (result: CopyResult) => void;
}) {
  if (step.type !== StepType.RCO_SEGMENT) {
    return;
  }

  // Enter segment-copy mode with the current step as source
  setSelectionMode({
    type: "node-copy",
    onNodeSelect: (nodeId: string) => {
      // Find the node that was clicked
      const sourceNode = steps.find((s) => s.id === nodeId);
      if (sourceNode?.type === StepType.RCO_SEGMENT && sourceNode.segment) {
        const result = copySegmentConfigurationToStep({
          step,
          sourceSegment: sourceNode.segment,
        });
        onCopyComputed(result);
      } else {
        toast(t("Please select a segment node to copy."), {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 5 }),
        });
        return;
      }

      // Return to normal mode
      setSelectionMode({ type: "normal" });
    },
  });
}

/**
 * Build a destination step that carries the source's segment definition while
 * keeping the destination's existing children attached. Returns any destination
 * child IDs that no longer fit (their subtrees should be deleted by the caller).
 *
 * The shape can change in either direction (binary ↔ n-ary), so we always
 * rebuild the connection fields rather than spreading the destination step. For
 * n-ary segments we also regenerate edge IDs since the source's segment already
 * owns the originals.
 */
function copySegmentConfigurationToStep({
  step,
  sourceSegment,
}: {
  step: PartialWithRequiredType<RcoSegmentStep>;
  sourceSegment: NonNullable<
    PartialWithRequiredType<RcoSegmentStep>["segment"]
  >;
}): CopyResult {
  const sourceCharacteristic = sourceSegment.segmentableCharacteristic;
  const destCharacteristic = step.segment?.segmentableCharacteristic;
  const sourceIsNary =
    sourceCharacteristic !== undefined &&
    narySegmentCharacteristics.has(sourceCharacteristic);
  const destIsNary =
    destCharacteristic !== undefined &&
    narySegmentCharacteristics.has(destCharacteristic);

  const destArmCount = destIsNary ? (step.edges?.length ?? 0) : 2;

  // Parse to a strict n-ary segment when needed — PartialWithRequiredType makes
  // every field optional, so this is the only way to access conditions without
  // `as` casts.
  const parsedNarySource = sourceIsNary
    ? DeliveryLocationNarySegmentSchema.safeParse(sourceSegment)
    : undefined;
  const narySourceData = parsedNarySource?.success
    ? parsedNarySource.data
    : undefined;
  const sourceArmCount = sourceIsNary
    ? narySourceData
      ? narySourceData.conditions.length + 1
      : 0
    : 2;

  // Ordered destination children: [condition slots..., default slot].
  // Binary destinations follow the same convention used in
  // handleCharacteristicSelected: nonMatchingNextId is the (only) condition,
  // matchingNextId is the default.
  let destConditionNextIds: (string | undefined)[];
  let destDefaultNextId: string | undefined;
  if (destIsNary) {
    const conditionEdges = (step.edges ?? []).filter(
      (e) => e.edgeId !== step.defaultEdgeId,
    );
    const defaultEdge = (step.edges ?? []).find(
      (e) => e.edgeId === step.defaultEdgeId,
    );
    destConditionNextIds = conditionEdges.map((e) => e.nextId);
    destDefaultNextId = defaultEdge?.nextId;
  } else {
    destConditionNextIds = [step.nonMatchingNextId];
    destDefaultNextId = step.matchingNextId;
  }

  if (!sourceIsNary) {
    // Target shape: binary. Map first destination condition → nonMatching,
    // default → matching. Any further destination conditions are excess.
    const excessChildIds = destConditionNextIds
      .slice(1)
      .filter((id): id is string => isDef(id) && id !== "");
    return {
      newStep: {
        ...step,
        segment: sourceSegment,
        edges: undefined,
        defaultEdgeId: undefined,
        matchingNextId: destDefaultNextId,
        nonMatchingNextId: destConditionNextIds[0],
      },
      excessChildIds,
      sourceArmCount,
      destArmCount,
    };
  }

  // Target shape: n-ary. We must rebuild edges with fresh edge IDs and rewrite
  // the source segment's conditions to use those same IDs, since the source's
  // original edge IDs are still in use by the source step.
  if (!narySourceData) {
    // Source's n-ary segment isn't a known/valid shape — fall back to a plain
    // segment swap rather than corrupting edges.
    return {
      newStep: { ...step, segment: sourceSegment },
      excessChildIds: [],
      sourceArmCount,
      destArmCount,
    };
  }

  const newDefaultEdgeId = new ObjectId().toString();
  const rewrittenConditions = narySourceData.conditions.map((condition) => ({
    ...condition,
    edgeId: new ObjectId().toString(),
  }));
  const rewrittenSegment: DeliveryLocationNarySegment = {
    ...narySourceData,
    conditions: rewrittenConditions,
  };

  const newEdges = rewrittenConditions.map((condition, i) => ({
    edgeId: condition.edgeId,
    nextId: destConditionNextIds[i] ?? "",
  }));
  newEdges.push({ edgeId: newDefaultEdgeId, nextId: destDefaultNextId ?? "" });

  const excessChildIds = destConditionNextIds
    .slice(rewrittenConditions.length)
    .filter((id): id is string => isDef(id) && id !== "");

  return {
    newStep: {
      ...step,
      segment: rewrittenSegment,
      edges: newEdges,
      defaultEdgeId: newDefaultEdgeId,
      matchingNextId: undefined,
      nonMatchingNextId: undefined,
    },
    excessChildIds,
    sourceArmCount,
    destArmCount,
  };
}

function handleEnterSubtreeCopyMode({
  steps,
  setSelectionMode,
  step,
  setAutomation,
}: {
  steps: PartialWithRequiredType<CheckoutOptimizationStep>[];
  setSelectionMode: (selectionMode: NodeSelectionMode) => void;
  step: PartialWithRequiredType<RcoSegmentStep>;
  setAutomation:
    | React.Dispatch<React.SetStateAction<FlowInProgress | null>>
    | undefined;
}) {
  if (step.type !== StepType.RCO_SEGMENT) {
    return;
  }

  if (!setAutomation) {
    throw new Error("setAutomation is required");
  }

  const onNodeSelect = async (nodeId: string) => {
    // Find the node that was clicked
    const sourceNode = steps.find((s) => s.id === nodeId);
    if (sourceNode?.type === StepType.RCO_SEGMENT) {
      try {
        // Copy the subtree in frontend
        const result = copySubtree({
          steps,
          sourceNodeId: nodeId,
          targetNodeId: step.id,
        });
        if (!result.success) {
          toast(result.errorMessage, {
            variant: "error",
            duration: Temporal.Duration.from({ seconds: 5 }),
          });
          return;
        }

        setAutomation((prev) => {
          if (!prev) {
            return prev;
          }

          return { ...prev, steps: result.newSteps };
        });

        toast(t("Successfully copied segment subtree."), {
          variant: "success",
          duration: Temporal.Duration.from({ seconds: 5 }),
        });
      } catch (error) {
        console.error("Failed to copy segment subtree.", error);
        toast(t("Failed to copy segment subtree."), {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 5 }),
        });
      }
    } else {
      toast(t("Please select a segment node to copy."), {
        variant: "error",
        duration: Temporal.Duration.from({ seconds: 5 }),
      });
      return;
    }

    // Return to normal mode
    setSelectionMode({ type: "normal" });
  };

  // Enter subtree-copy mode
  setSelectionMode({ type: "node-copy-subtree", onNodeSelect });
}

const ClickToCopyExistingSegment = memo(function ClickToCopyExistingSegment({
  steps,
  step,
  onEnterNodeSelectMode,
  onEnterSubtreeCopyMode,
  onExitNodeSelectMode,
  selectionMode,
}: {
  steps: PartialWithRequiredType<CheckoutOptimizationStep>[];
  step: PartialWithRequiredType<CheckoutOptimizationStep>;
  onEnterNodeSelectMode: () => void;
  onEnterSubtreeCopyMode: () => void;
  onExitNodeSelectMode: () => void;
  selectionMode: NodeSelectionMode;
}) {
  const { t } = useTranslation();
  const hasExistingSegment = useMemo(() => {
    return steps.some(
      (s) => s.type === StepType.RCO_SEGMENT && s.id !== step.id,
    );
  }, [step, steps]);

  if (!hasExistingSegment) {
    return null;
  }

  return selectionMode.type === "normal" ? (
    <>
      <Flex
        borderBottomWidth="1px"
        borderColor="primary"
        borderStyle="solid"
        p="none"
        w="full"
      />
      <Flex align="center" gap="xs">
        <Text fontSize="xs" textColor="secondary">
          {t("or")}
        </Text>
        <RedoButton
          hierarchy="secondary"
          onClick={onEnterNodeSelectMode}
          size="xs"
          text={t("copy existing segment configuration")}
        />
      </Flex>
      <Flex align="center" gap="xs">
        <Text fontSize="xs" textColor="secondary">
          {t("or")}
        </Text>
        <RedoButton
          hierarchy="secondary"
          onClick={onEnterSubtreeCopyMode}
          size="xs"
          text={t("copy existing segment and subtree")}
        />
      </Flex>
    </>
  ) : selectionMode.type === "node-copy-subtree" ? (
    <>
      <Text fontSize="xs" textColor="secondary">
        {t("Click a segment node in the tree to copy it and its subtree")}
      </Text>
      <RedoButton
        hierarchy="secondary"
        onClick={onExitNodeSelectMode}
        size="xs"
        text={t("Cancel")}
      />
    </>
  ) : selectionMode.type === "node-copy" ? (
    <>
      <Text fontSize="xs" textColor="secondary">
        {t("Click a segment node in the tree to copy its configuration")}
      </Text>
      <RedoButton
        hierarchy="secondary"
        onClick={onExitNodeSelectMode}
        size="xs"
        text={t("Cancel")}
      />
    </>
  ) : (
    assertNever(selectionMode)
  );
});

export const rcoSegmentStepType: FrontendStepType<
  PartialWithRequiredType<RcoSegmentStep>
> = {
  type: "custom",
  // TODO: validate each segment type
  valid: (step) => validateSegment({ step }),
  selectable: (_) => true,
  customInspector: RcoSegmentInspector,
};

function validateSegment({
  step,
}: {
  step: PartialWithRequiredType<RcoSegmentStep>;
}): boolean {
  if (!step.segment || !step.segment.segmentableCharacteristic) {
    return false;
  }

  if (narySegmentCharacteristics.has(step.segment.segmentableCharacteristic)) {
    if (!hasAllEdgesConnected(step)) {
      return false;
    }
  } else if (!step.matchingNextId || !step.nonMatchingNextId) {
    return false;
  }

  switch (step.segment.segmentableCharacteristic) {
    case SegmentableCheckoutCharacteristic.DATETIME_RANGE: {
      if (!step.segment.startDateTime || !step.segment.endDateTime) {
        return false;
      }
      const startPlainDateTime = Temporal.PlainDateTime.from(
        step.segment.startDateTime,
      );
      const endPlainDateTime = Temporal.PlainDateTime.from(
        step.segment.endDateTime,
      );
      return (
        Temporal.PlainDateTime.compare(startPlainDateTime, endPlainDateTime) < 0
      );
    }
    case SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY:
      return CheckoutProbabilitySegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE:
      return CustomerReturnRateSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.CUSTOMER_TAGS:
      return CustomerTagsSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES:
      return DeliveryDistanceMilesSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION:
      return DeliveryLocationSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_RULES:
      return DeliveryLocationRulesSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.ITEM_QUANTITY:
      return ItemQuantitySegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES:
      return LineItemPropertiesSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.ORDER_COUNT:
      return OrderCountSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.HISTORICAL_ORDER_VALUE:
      return HistoricalOrderValueSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.ORIGIN_LOCATION:
      return OriginLocationSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS:
      return ProductCollectionsSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.PRODUCT_SKU:
      return ProductSkuSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.PRODUCT_TAGS:
      return ProductTagsSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.PRODUCT_VENDOR:
      return ProductVendorSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.TIME_OF_DAY:
      return TimeOfDaySegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.PRODUCT_METAFIELDS:
      return ProductMetafieldsSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.CART_VALUE:
      return CartValueSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.SHIPMENT_WEIGHT:
      return ShipmentWeightSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.CROSS_BORDER_SHIPMENT:
      return CrossBorderShipmentSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.PO_BOX_SHIPMENT:
      return PoBoxShipmentSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.LCG_ELIGIBLE_SHIPMENT:
      return LcgEligibleShipmentSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.ADDRESS_PROPERTIES:
      return AddressPropertiesSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026:
      return isValidDeliveryLocationNarySegment(step.segment);
    case SegmentableCheckoutCharacteristic.COMPOUND:
      return CompoundNarySegmentSchema.safeParse(step.segment).success;
    default:
      assertNever(step.segment.segmentableCharacteristic);
  }
}

function hasAllEdgesConnected(
  step: PartialWithRequiredType<RcoSegmentStep>,
): boolean {
  if (!step.edges || step.edges.length === 0) {
    return false;
  }
  return step.edges.every((edge) => edge.nextId && edge.nextId !== "");
}

function isValidDeliveryLocationNarySegment(segment: unknown): boolean {
  const result = DeliveryLocationNarySegmentSchema.safeParse(segment);
  if (!result.success) {
    return false;
  }
  return (
    result.data.conditions.length > 0 &&
    result.data.conditions.every((c) => c.locationRules.length > 0)
  );
}
